import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ExternalLink, ArrowUpRight, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLOR = {
    ORDER_PLACED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    ORDER_MATCHED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    MARGIN_ADDED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    EARLY_EXIT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    LIQUIDATION: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    SETTLEMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
};

const fmtAgo = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (!t) return "—";
    const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

export default function LiveOnChainFeed({ limit = 5 }) {
    const [events, setEvents] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [latestId, setLatestId] = useState(null);
    const seen = useRef(new Set());

    const load = async () => {
        try {
            const r = await axios.get(`${API}/public/onchain/tape?limit=${limit}`);
            const data = Array.isArray(r.data) ? r.data : [];
            setEvents(data);
            if (data.length && !seen.current.has(data[0].id)) {
                setLatestId(data[0].id);
            }
            data.forEach((e) => seen.current.add(e.id));
        } catch {
            // network hiccup — keep prior data
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            data-testid="live-onchain-feed"
            className="bg-[#0A0A0C]/80 backdrop-blur border border-[#1F1F24] rounded-sm overflow-hidden"
        >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1F1F24]">
                <div className="flex items-center gap-2">
                    <span className="relative flex w-2 h-2">
                        <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-60" />
                        <span className="relative w-2 h-2 rounded-full bg-cyan-400" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-300">
                        Live on-chain · Hyperliquid testnet
                    </span>
                </div>
                <Link
                    to="/onchain"
                    data-testid="live-feed-view-all"
                    className="inline-flex items-center gap-1 text-[10px] font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                    View all
                    <ArrowUpRight className="w-3 h-3" />
                </Link>
            </div>

            <div className="divide-y divide-[#16161A] min-h-[180px]">
                {!loaded && (
                    <div className="px-4 py-8 text-center text-[11px] font-mono text-zinc-600">
                        connecting to on-chain feed…
                    </div>
                )}
                {loaded && events.length === 0 && (
                    <div className="px-4 py-8 text-center text-[11px] font-mono text-zinc-600 flex items-center justify-center gap-2">
                        <Activity className="w-3.5 h-3.5 opacity-60" />
                        waiting for the next testnet event…
                    </div>
                )}
                {events.map((ev) => {
                    const color = EVENT_COLOR[ev.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-700/10";
                    const isNew = ev.id === latestId;
                    return (
                        <a
                            key={ev.id}
                            href={ev.explorer_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`live-feed-row-${ev.id}`}
                            className={`group flex items-center gap-3 px-4 py-2.5 hover:bg-[#0F0F12] transition-colors ${
                                isNew ? "animate-pulse-once" : ""
                            }`}
                        >
                            <span
                                className={`shrink-0 text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border ${color}`}
                            >
                                {ev.event_type.replace("_", " ")}
                            </span>
                            {ev.metadata?.coin && (
                                <span className="font-mono text-[11px] text-zinc-200 shrink-0">
                                    {ev.metadata.coin}
                                </span>
                            )}
                            <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                                {fmtAgo(ev.attempted_at)}
                            </span>
                            <div className="flex-1" />
                            <span className="hidden sm:inline font-mono text-[10px] text-cyan-400/70 group-hover:text-cyan-300 truncate max-w-[120px]">
                                {ev.tx_hash ? `${ev.tx_hash.slice(0, 8)}…${ev.tx_hash.slice(-4)}` : "—"}
                            </span>
                            <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-cyan-400 shrink-0" />
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
